import { createApp } from 'vue';
import App from './App.vue';
import router from './router';
import { createPinia } from 'pinia';
import './style.css';
import { FontAwesomeIcon } from './utils/iconLibrary.js';
import { createAuth0 } from '@auth0/auth0-vue';
import { Browser } from '@capacitor/browser';
import { App as CapacitorApp } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { setupAxiosInterceptors } from './lib/setupInterceptors.js';
import { initAppInsights } from './telemetry/appInsights.js';
import { useMyStore } from './store.js';
import { initDeepLinks } from './deep-links.js';
import { Purchases, LOG_LEVEL } from '@revenuecat/purchases-capacitor';
import { isTokenExpired } from './utils/token.js';

if (Capacitor.isNativePlatform()) {
  const token = localStorage.getItem('authToken');
  if (token && isTokenExpired(token, 60)) {
    console.log('[Boot] Token expired — wiping before Auth0 init');
    Object.keys(localStorage)
      .filter((k) => k.startsWith('@@auth0spajs@@') || k.startsWith('auth0.'))
      .forEach((k) => localStorage.removeItem(k));
    localStorage.removeItem('authToken');
    localStorage.removeItem('userInfo');
    localStorage.removeItem('userLoggedIn');
    localStorage.removeItem('appBackgroundedAt');
    sessionStorage.clear();
    sessionStorage.setItem('force_manual_login', '1');
  }
}

const app = createApp(App);
const pinia = createPinia();
app.use(pinia);
app.use(router);
app.component('font-awesome-icon', FontAwesomeIcon);

const isNative = Capacitor.isNativePlatform();
const bundleId = isNative ? import.meta.env.VITE_BUNDLE_ID : 'web';
const redirectUri = isNative
  ? `${bundleId}://${import.meta.env.VITE_AUTH0_DOMAIN}/ios/${bundleId}/callback`
  : `${window.location.origin}/callback`;
const store = useMyStore(pinia);
const revCatApiKey = import.meta.env.VITE_REVENUECAT_IOS_KEY;
const baseURL = import.meta.env.VITE_BASE_URL;

console.log({ bundleId, redirectUri, native: isNative });
console.log(
  'AUTH0 ENV',
  import.meta.env.VITE_AUTH0_DOMAIN,
  import.meta.env.VITE_AUTH0_CLIENT_ID
);

app.use(
  createAuth0({
    domain: import.meta.env.VITE_AUTH0_DOMAIN,
    clientId: import.meta.env.VITE_AUTH0_CLIENT_ID,
    authorizationParams: {
      redirect_uri: redirectUri,
      audience: import.meta.env.VITE_AUTH0_AUDIENCE,
      scope: 'openid profile email',
      // prompt: 'login'
    },
    cacheLocation: 'localstorage',
    // advancedOptions: {
    //   openUrl: async (url) => {
    //     await Browser.open({ url });
    //   },
    // },
    async onRedirectCallback(appState) {
      try {
        await router.replace(appState?.targetUrl || '/');
      } catch (error) {
        console.error('Redirect callback error:', error);
      }
    },
  })
);

const wipeAuthState = () => {
  Object.keys(localStorage)
    .filter((k) => k.startsWith('@@auth0spajs@@') || k.startsWith('auth0.'))
    .forEach((k) => localStorage.removeItem(k));
  localStorage.removeItem('authToken');
  localStorage.removeItem('userInfo');
  localStorage.removeItem('userLoggedIn');
  localStorage.removeItem('appBackgroundedAt');

  sessionStorage.removeItem('force_manual_login');
  sessionStorage.removeItem('auth0_fresh_login');

  sessionStorage.setItem('force_manual_login', '1');
  store.updateUserLoggedIn(false);
  store.setAuthInitialized(false);
  store.setUserDataLoaded(false);
};

if (Capacitor.isNativePlatform()) {
  CapacitorApp.addListener('appStateChange', async ({ isActive }) => {
    if (!isActive) return;

    if (sessionStorage.getItem('auth0_handling') === '1') {
      console.log('[AppState] Auth in progress — skipping check');
      return;
    }

    const cameFromPlaid =
      typeof window !== 'undefined' &&
      window.location?.pathname?.includes('plaid-oauth-redirect');
    const hasReceivedRedirect = !!(
      window.localStorage.getItem('receivedRedirectUri') || null
    );

    if (cameFromPlaid || hasReceivedRedirect) {
      console.log('[AppState] EARLY RETURN — Plaid OAuth path');
      return;
    }

    const token = localStorage.getItem('authToken');
    if (!token) {
      console.log('[AppState] No token — not logged in, doing nothing');
      return;
    }
    if (isTokenExpired(token, 60)) {
      console.log('[AppState] Token expired — forcing re-auth');
      wipeAuthState();
      window.location.href = '/';
      // router.replace({ name: 'SignIn' });
      return;
    }

    console.log('[AppState] Token valid — resuming normally');

    try {
      const { useAppUpdateCheck } = await import(
        '@/composables/useAppUpdateCheck'
      );
      await useAppUpdateCheck().checkForUpdate();
    } catch (err) {
      console.warn('Update check failed on resume:', err);
    }
  });
}

// CapacitorApp.addListener('appStateChange',async ({ isActive }) => {
//  if (!isActive) return;

//  console.log('App was opened');

//  const auth0 = app.config.globalProperties.$auth0;

//  console.log('auth0.isAuthenticated.value ' + auth0.isAuthenticated.value);

//  if (!auth0.isAuthenticated.value) {
//     console.log('inside if no auth value? ');

//     window.location.reload();
//  }
// });

// CapacitorApp.addListener('appStateChange',async ({ isActive }) => {
//   if (!isActive) return;

//   console.log('App was opened');

//   const auth0 = app.config.globalProperties.$auth0;

//   const isHandlingAuth = sessionStorage.getItem('auth0_handling');

//   if (isHandlingAuth == null) {
//     console.log('No auth and not processing');
//     window.location.reload();
//   }
// });

// if (Capacitor.isNativePlatform()) {
//   CapacitorApp.addListener('appStateChange', async ({ isActive }) => {
//     if (!isActive) {
//       localStorage.setItem('appBackgroundedAt', String(Date.now()));
//       return;
//     }

//     const cameFromPlaid =
//       typeof window !== 'undefined' &&
//       window.location?.pathname?.includes('plaid-oauth-redirect');
//     const hasReceivedRedirect = !!(
//       window.localStorage.getItem('receivedRedirectUri') || null
//     );

//     if (cameFromPlaid || hasReceivedRedirect) {
//       console.log('Skipping reload: returning from Plaid OAuth');
//       return;
//     }

//     //leave here for testing purposes change the if condition to minutesBackgrounded > 5
//     // const backgroundedAt = Number(localStorage.getItem('appBackgroundedAt') || 0);
//     // const minutesBackgrounded = (Date.now() - backgroundedAt) / (1000 * 60);

//     const backgroundedAt = Number(localStorage.getItem('appBackgroundedAt') || 0);
//     const hoursBackgrounded = (Date.now() - backgroundedAt) / (1000 * 60 * 60);

//     if (hoursBackgrounded > 1) {
//       console.log('App backgrounded too long — forcing clean state');
//       Object.keys(localStorage)
//         .filter(k => k.startsWith('@@auth0spajs@@') || k.startsWith('auth0.'))
//         .forEach(k => localStorage.removeItem(k));
//       localStorage.removeItem('authToken');
//       localStorage.removeItem('userInfo');
//       localStorage.removeItem('userLoggedIn');
//       localStorage.removeItem('appBackgroundedAt');

//       sessionStorage.clear();
//       sessionStorage.setItem('force_manual_login', '1');
//       store.updateUserLoggedIn(false);
//       store.setAuthInitialized(false);
//       store.setUserDataLoaded(false);
//       router.replace({ name: 'SignIn' });
//       return;
//     }

//     const isHandlingAuth = sessionStorage.getItem('auth0_handling');
//     const hasAuthToken = localStorage.getItem('authToken');

//     if (isHandlingAuth == null && !hasAuthToken) {
//       console.log('No auth token and not processing - reloading');
//       window.location.reload();
//       return;
//     }

//     try {
//       const { useAppUpdateCheck } = await import('@/composables/useAppUpdateCheck');
//       await useAppUpdateCheck().checkForUpdate();
//     } catch (err) {
//       console.warn('Update check failed on resume:', err);
//     }
//   })
// }

CapacitorApp.addListener('appUrlOpen', async ({ url }) => {
  try {
    if (!url) return;

    const u = new URL(url);
    if (u.protocol !== `${bundleId}:`) return;

    // ── Logout callback ─────────────────────────────────────────────
    if (u.hostname === 'logout') {
      try {
        await Browser.close();
      } catch {}
      sessionStorage.removeItem('auth0_handling');
      await router.replace({ name: 'SignIn' });
      return;
    }

    const q = u.searchParams;
    const h = new URLSearchParams(u.hash?.replace(/^#/, '') || '');
    const hasCode = q.has('code') || h.has('code');
    const hasState = q.has('state') || h.has('state');

    // ── Logout return (no code/state) ─────────────────────────────
    if (!hasCode || !hasState) {
      console.log('[appUrlOpen] No code/state - checking if logout');
      const isLogoutReturn =
        sessionStorage.getItem('logout_in_progress') === '1';

      if (isLogoutReturn) {
        console.log('[appUrlOpen] Logout return detected');

        try {
          await Browser.close();
          console.log('[appUrlOpen] Browser closed');
        } catch (e) {
          console.log('[appUrlOpen] Browser close error (ok):', e);
        }

        sessionStorage.removeItem('logout_in_progress');
        sessionStorage.removeItem('auth0_handling');
        localStorage.clear();

        try {
          const { Preferences } = await import('@capacitor/preferences');
          await Preferences.clear();
        } catch {}

        window.location.href = '/';
      }
      return;
    }

    // ── Auth callback (code + state present) ────────────────────────
    const auth0 = app.config.globalProperties.$auth0;

    sessionStorage.setItem('auth0_handling', '1');

    const appState = await auth0.handleRedirectCallback(url);

    console.log('app state ' + JSON.stringify(appState));

    // ✅ CRITICAL FIX: Get token AND store it before navigation
    const token = await auth0.getAccessTokenSilently({
      authorizationParams: { audience: import.meta.env.VITE_AUTH0_AUDIENCE },
    });

    localStorage.setItem('authToken', token);

    // Get user info from Auth0 (already populated after handleRedirectCallback)
    const userInfo = auth0.user.value;
    if (userInfo) {
      localStorage.setItem('userInfo', JSON.stringify(userInfo));
    }

    // ✅ Mark as fresh login so App.vue knows to proceed immediately
    sessionStorage.setItem('auth0_fresh_login', '1');

    // Close browser before navigation
    try {
      await Browser.close();
    } catch {}

    console.log('after try close browser');

    // Navigate to target or home
    await router.replace(appState?.targetUrl || '/');

    // Clear handling flag after successful navigation
    sessionStorage.removeItem('auth0_handling');
  } catch (error) {
    console.error('Auth callback failed:', error);
    wipeAuthState();
    router.replace('/auth-error');
  }
});

// CapacitorApp.addListener('appUrlOpen', async ({ url }) => {
//   console.log('app url is open');
//   try {
//     if (!url) return;

//     const u = new URL(url);
//     if (u.protocol !== `${bundleId}:`) return;

//     console.log('hostname ' + JSON.stringify(u));

//     //adding this jan 6th 26 to fix the logout on ios going to the default browser
//     if (u.hostname === 'logout') {
//       try {
//         await Browser.close();
//       } catch {
//         //ignore- browser may already be closed
//       }
//       sessionStorage.removeItem('auth0_handling');
//       await router.replace({ name: 'SignIn' });
//       return;
//     }

//     const q = u.searchParams;
//     const h = new URLSearchParams(u.hash?.replace(/^#/, '') || '');
//     const hasCode = q.has('code') || h.has('code');
//     const hasState = q.has('state') || h.has('state');
//     if (!hasCode || !hasState) return;

//     const auth0 = app.config.globalProperties.$auth0;

//     sessionStorage.setItem('auth0_handling', '1');

//     const appState = await auth0.handleRedirectCallback(url);

//     console.log('app state ' + JSON.stringify(appState));

//     await auth0.getAccessTokenSilently({
//       authorizationParams: { audience: import.meta.env.VITE_AUTH0_AUDIENCE },
//     });
//     console.log('after get access token');

//     await router.replace(appState?.targetUrl || '/');

//     console.log('after router replace');

//     try {
//       await Browser.close();
//     } catch {
//       //ignore - browser may already be closed
//     }

//     console.log('after try close browser');
//   } catch {
//     sessionStorage.removeItem('auth0_tx');
//     sessionStorage.removeItem('auth0_handling');
//     router.replace('/auth-error');
//   }
// });

console.log(
  'enable appinstights prod ' + import.meta.env.VITE_ENABLE_APPINSIGHTS_PROD
);
console.log(
  'enable app insights QA ' + import.meta.env.VITE_ENABLE_APPINSIGHTS_QA
);

if (isNative && import.meta.env.VITE_ENABLE_APPINSIGHTS_PROD === 'true') {
  initAppInsights();
}

if (isNative && import.meta.env.VITE_ENABLE_APPINSIGHTS_QA === 'true') {
  initAppInsights();
}

setupAxiosInterceptors();
initDeepLinks();

const configureRevenueCat = async () => {
  if (!isNative) return;
  try {
    await Purchases.setLogLevel({ level: LOG_LEVEL.WARN });
    if (Capacitor.getPlatform() === 'ios') {
      const storedUserInfo = localStorage.getItem('userInfo');
      const userSub = storedUserInfo ? JSON.parse(storedUserInfo).sub : null;

      await Purchases.configure({
        apiKey: revCatApiKey,
        appUserID: userSub ?? undefined,
      });
    }
  } catch (e) {
    console.log('RC config error ' + e.message);
  }
};

// const configureRevenueCat = async () => {
//   console.log('[RC] configureRevenueCat START, isNative:', isNative);
//   alert('[RC] API Key1:', import.meta.env.VITE_REVENUECAT_IOS_KEY);
//   alert('baseurl ' + baseURL);
//   alert('apiKeyRev ' + revCatApiKey);
//   if (!isNative) return;
//   try {
//     console.log('[RC] API Key:', import.meta.env.VITE_REVENUECAT_IOS_KEY);
//     await Purchases.setLogLevel({ level: LOG_LEVEL.DEBUG });
//     const platform = Capacitor.getPlatform();
//     console.log('[RC] Platform:', platform);
//     if (platform === 'ios') {
//       await Purchases.configure({
//         apiKey: revCatApiKey,
//       });
//       console.log('[RC] ✅ RevenueCat configured successfully');
//     }
//   } catch (e) {
//     console.error('[RC] ❌ Configuration error:', e);
//   }
// };

// const configureRevenueCat = async () => {
//   console.log('configureRevenueCat called, isNative:', isNative);
//   if (!isNative) return;
//   try {
//     console.log('RC API Key:', import.meta.env.VITE_REVENUECAT_IOS_KEY);
//     await Purchases.setLogLevel({ level: LOG_LEVEL.DEBUG }); // remove in production
//     const platform = Capacitor.getPlatform();
//     console.log('Platform:', platform);
//     if (platform === 'ios') {
//       await Purchases.configure({
//         apiKey: import.meta.env.VITE_REVENUECAT_IOS_KEY,
//       });
//       console.log('RevenueCat SDK configured successfully!');
//     }
//     console.log('RevenueCat SDK configured!');
//   } catch (e) {
//     console.error('RevenueCat configuration error:', e);
//     console.error('Error details:', JSON.stringify(e));
//   }
// };

router.isReady().then(async () => {
  await configureRevenueCat();
  app.mount('#app');
});

// app.mount('#app');
